import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLexware } from '../context/LexwareContext';
import CustomerDetailsModal from '../components/customers/CustomerDetailsModal';
import Pagination from '../components/common/Pagination';

const { FiSearch, FiPlus, FiAlertTriangle, FiAlertCircle, FiClock, FiCheckCircle, FiUserX } = FiIcons;

function Customers() {
  const { data } = useLexware();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
  const [filterNoContacts, setFilterNoContacts] = useState(false);

  const filteredCustomers = useMemo(() => {
    let customers = data.customers || [];
    const contacts = data.contacts || [];

    // 1. Text Filter
    if (searchTerm) {
      customers = customers.filter(customer =>
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // 2. Filter: Kunden OHNE Ansprechpartner
    if (filterNoContacts) {
      customers = customers.filter(cust => 
        !contacts.some(cont => cont.customerId === cust.id)
      );
    }
    return customers;
  }, [data.customers, data.contacts, searchTerm, filterNoContacts]);

  const sortedCustomers = useMemo(() => {
    let sortableItems = [...filteredCustomers];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        let valA = a[sortConfig.key];
        let valB = b[sortConfig.key];
        
        if (sortConfig.key === 'id') {
          const numA = parseInt(valA.replace(/\D/g, '')) || 0;
          const numB = parseInt(valB.replace(/\D/g, '')) || 0;
          return sortConfig.direction === 'asc' ? numA - numB : numB - numA;
        }

        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [filteredCustomers, sortConfig]);

  const paginatedCustomers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedCustomers.slice(start, start + pageSize);
  }, [sortedCustomers, currentPage, pageSize]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const getStatusConfig = (customer) => {
    const overdue = customer.overdueDays;
    const gap = customer.avgGapDays;

    if (!overdue || overdue <= 0) return { color: 'green', label: 'In Zeit', icon: null, rowClass: 'hover:bg-blue-50' };
    if (overdue <= 10) return { color: 'yellow', label: 'Beobachtung', icon: FiClock, rowClass: 'bg-yellow-50/30' };
    if (overdue <= gap) return { color: 'orange', label: 'Verzögerung', icon: FiAlertCircle, rowClass: 'bg-orange-50/40' };
    return { color: 'red', label: 'Kritisch', icon: FiAlertTriangle, rowClass: 'bg-red-50/50' };
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Kunden</h1>
        <div className="flex gap-2">
          <button 
            onClick={() => { setFilterNoContacts(!filterNoContacts); setCurrentPage(1); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all border ${filterNoContacts ? 'bg-orange-600 text-white border-orange-700 shadow-lg shadow-orange-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
          >
            <SafeIcon icon={FiUserX} /> Ohne Ansprechpartner
            {filterNoContacts && <span className="ml-1 bg-white/20 px-1.5 rounded">{filteredCustomers.length}</span>}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-200 text-xs font-bold">
            <SafeIcon icon={FiPlus} /> Neuer Kunde
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="relative">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input 
            type="text" 
            placeholder="Kunden durchsuchen..." 
            value={searchTerm} 
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} 
            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 transition-all outline-none" 
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th onClick={() => requestSort('id')} className="px-6 py-4 text-left text-xs font-black text-gray-400 uppercase tracking-widest cursor-pointer hover:text-blue-600">Kd-Nr.</th>
                <th onClick={() => requestSort('name')} className="px-6 py-4 text-left text-xs font-black text-gray-400 uppercase tracking-widest cursor-pointer hover:text-blue-600">Kunde</th>
                <th className="px-6 py-4 text-left text-xs font-black text-gray-400 uppercase tracking-widest">Kontakt</th>
                <th onClick={() => requestSort('totalValue')} className="px-6 py-4 text-right text-xs font-black text-gray-400 uppercase tracking-widest cursor-pointer hover:text-blue-600">Umsatz (Netto)</th>
                <th className="px-6 py-4 text-right text-xs font-black text-gray-400 uppercase tracking-widest">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedCustomers.map((customer) => {
                const status = getStatusConfig(customer);
                const hasContact = data.contacts?.some(c => c.customerId === customer.id);
                return (
                  <tr key={customer.id} className={`${status.rowClass} transition-colors group`}>
                    <td className="px-6 py-4 font-mono text-xs font-bold text-gray-400">{customer.id}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center font-black text-xs text-blue-600 shadow-sm">
                          {customer.name?.charAt(0)}
                        </div>
                        <span className="font-bold text-gray-900">{customer.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {hasContact ? (
                        <div className="flex items-center gap-1.5 text-green-600 font-bold text-xs uppercase">
                          <SafeIcon icon={FiCheckCircle} /> Vorhanden
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-orange-500 font-bold text-xs uppercase">
                          <SafeIcon icon={FiAlertTriangle} /> Fehlt
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right font-black text-gray-900">
                      €{customer.totalValue.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => setSelectedCustomer(customer)} className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs font-bold hover:bg-blue-600 hover:text-white transition-all">
                        Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Integration */}
        <Pagination 
          currentPage={currentPage}
          totalItems={filteredCustomers.length}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      </div>

      <AnimatePresence>
        {selectedCustomer && <CustomerDetailsModal customer={selectedCustomer} onClose={() => setSelectedCustomer(null)} />}
      </AnimatePresence>
    </div>
  );
}

export default Customers;